import React, { useMemo } from 'react'
import { Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { MessageSquare } from 'lucide-react'
import { CommentList } from './CommentList'
import { CommentForm } from './CommentForm'
import type { StatementColumn } from '../../types'
import type { EnhancedStatementRow } from '../../../financialStatements/utils/dataEnhancement'
import { useCtxCommentsMap } from '../../queries/useCtxCommentsMap'
import { getCellKey, buildDimensionQuery } from '../../utils/commentUtils'
import { useCreateComment } from '../../queries/useCreateComment'
import { useDeleteComment } from '../../queries/useDeleteComment'
import { useUpdateComment } from '../../queries/useUpdateComment'

interface CommentModalProps {
  open: boolean
  onClose: () => void
  column: StatementColumn | null
  row: EnhancedStatementRow | null
}

const { Title, Text } = Typography

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Separator = styled.span`
  color: #d9d9d9;
  margin: 0 4px;
`

/**
 * Modal for viewing and managing comments for a specific cell
 */
export const CommentModal: React.FC<CommentModalProps> = ({ open, column, row, onClose }) => {
  const { t } = useTranslation()
  const { mutateAsync: createCommentMutation } = useCreateComment()
  const { mutateAsync: updateCommentMutation } = useUpdateComment()
  const { mutateAsync: deleteCommentMutation } = useDeleteComment()
  const { data: commentMap } = useCtxCommentsMap()
  const commentCellKey = getCellKey(row, column)

  const comments = useMemo(() => {
    if (!commentCellKey || !commentMap) return []
    return commentMap.get(commentCellKey) ?? []
  }, [commentMap, commentCellKey])

  const handleCreate = async (text: string) => {
    if (!row || !column) return

    // Build comment data from row and column (body data)
    const commentData: any = {
      text
    }

    // Add accountCode for account rows, or incomeStatementRowId for heading rows
    if (row._sourceAccountCode) {
      commentData.accountCode = row._sourceAccountCode
    } else if (row._sourceStatementRowId) {
      commentData.incomeStatementRowId = row._sourceStatementRowId
    }

    // Add fiscal year or period info based on column type
    if (column.periodGroup && 'id' in column.periodGroup) {
      // Fiscal year level comment
      commentData.fiscalYearId = column.periodGroup.id
    } else if (column.year && column.month) {
      // Period level comment
      commentData.year = column.year
      commentData.month = column.month
    }

    // Build dimension query if dimension exists (as query parameter)
    const dimensions = row.dimensionId ? buildDimensionQuery(row.dimensionId) : undefined

    await createCommentMutation({ comment: commentData, dimensions })
  }

  const handleUpdate = async (commentId: number, text: string) => {
    await updateCommentMutation({
      commentId,
      comment: { text }
    })
  }

  const handleDelete = async (commentId: number) => {
    await deleteCommentMutation(commentId)
  }

  // Generate modal title from column info
  const getTitle = () => {
    if (!column || !row) {
      return (
        <TitleRow>
          <MessageSquare size={18} />
          <span>{t('global:comments', 'Comments')}</span>
        </TitleRow>
      )
    }

    const parts: React.ReactNode[] = []

    // Add account name or code (or heading name)
    if (row.name && row._sourceAccountCode) {
      // Account row with code
      parts.push(<span key="account">{`${row._sourceAccountCode} - ${row.name}`}</span>)
    } else if (row.name) {
      // Heading row (no account code)
      parts.push(<span key="account">{row.name}</span>)
    } else if (row._sourceAccountCode) {
      // Account code only
      parts.push(<span key="account">{row._sourceAccountCode}</span>)
    }

    // Add dimension name if available
    if (row.dimensionName) {
      parts.push(<span key="dimension">{row.dimensionName}</span>)
    }

    // Add period info
    if (column.label) {
      parts.push(<span key="period">{column.label}</span>)
    }

    // Join parts with separator
    const content = parts.reduce<React.ReactNode[]>((acc, part, index) => {
      if (index > 0) {
        // eslint-disable-next-line react/no-array-index-key
        acc.push(<Separator key={`sep-${index}`}>•</Separator>)
      }
      acc.push(part)
      return acc
    }, [])

    return (
      <TitleRow>
        <MessageSquare size={18} />
        <span>{t('global:comments', 'Comments')}</span>
        {content.length > 0 && <Separator>•</Separator>}
        {content}
      </TitleRow>
    )
  }

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={700} title={getTitle()} maskClosable={false}>
      {/* Existing comments */}
      <CommentList comments={comments} onUpdate={handleUpdate} onDelete={handleDelete} />

      {/* New comment form */}
      <div>
        <Title level={5}>{t('global:addComment', 'Add comment')}</Title>
        <CommentForm onSubmit={handleCreate} submitText={t('global:add', 'Add')} />
      </div>
    </Modal>
  )
}
